<template>
  <v-container fluid class="pa-md-6">
    <!-- Dashboard Header -->
    <v-row class="mb-6">
      <v-col cols="12">
        <div class="d-flex align-center mb-4">
          <v-img src="@/assets/logo_gold_grass.png" max-height="60" max-width="60" class="mr-4"></v-img>
          <h1 class="text-h3 font-weight-bold primary--text">
            Bagu Rentals Dashboard
          </h1>
        </div>
        <p class="text-h6 grey--text text--darken-1">
          Welcome to your property management dashboard. Monitor your rentals, payments, and maintenance at a glance.
        </p>
      </v-col>
    </v-row>

    <!-- Statistics Cards -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="mx-auto" elevation="2" color="primary" dark>
          <v-card-text class="text-center">
            <v-icon size="48" class="mb-2">mdi-home-city-outline</v-icon>
            <div class="text-h4 font-weight-bold">{{ availableUnitsCount }}</div>
            <div class="text-subtitle-1">Available Units</div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="3">
        <v-card class="mx-auto" elevation="2" color="warning" dark>
          <v-card-text class="text-center">
            <v-icon size="48" class="mb-2">mdi-wrench-clock</v-icon>
            <div class="text-h4 font-weight-bold">{{ pendingRepairsCount }}</div>
            <div class="text-subtitle-1">Pending Repairs</div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="3">
        <v-card class="mx-auto" elevation="2" color="success" dark>
          <v-card-text class="text-center">
            <v-icon size="48" class="mb-2">mdi-credit-card-check</v-icon>
            <div class="text-h4 font-weight-bold">{{ totalPaymentsThisMonth }}</div>
            <div class="text-subtitle-1">Payments This Month</div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="3">
        <v-card class="mx-auto" elevation="2" color="error" dark>
          <v-card-text class="text-center">
            <v-icon size="48" class="mb-2">mdi-clock-alert</v-icon>
            <div class="text-h4 font-weight-bold">{{ incompletePaymentsCount }}</div>
            <div class="text-subtitle-1">Incomplete Payments</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Dashboard Content -->
    <v-row>
      <!-- Available Units -->
      <v-col cols="12" md="6" class="mb-6">
        <v-card elevation="2">
          <v-card-title class="d-flex align-center tableheader white--text">
            <v-icon left color="white">mdi-home-city-outline</v-icon>
            Available Units
            <v-spacer></v-spacer>
            <v-btn color="white" variant="text" to="/units" size="small">
              View All
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-list v-if="availableUnits.length > 0" lines="two" density="compact">
              <v-list-item v-for="unit in availableUnits.slice(0, 5)" :key="unit.id" class="px-4">
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-home</v-icon>
                </template>
                <v-list-item-title>{{ unit.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ unit.address }} - {{ formatCurrency(unit.monthly_rate) }}/month
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
            <v-alert v-else type="info" class="ma-3">
              No available units at the moment.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Pending Repairs -->
      <v-col cols="12" md="6" class="mb-6">
        <v-card elevation="2">
          <v-card-title class="d-flex align-center tableheader white--text">
            <v-icon left color="white">mdi-wrench-clock</v-icon>
            Pending Repairs
            <v-spacer></v-spacer>
            <v-btn color="white" variant="text" to="/maintenance-requests" size="small">
              View All
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-list v-if="pendingRepairs.length > 0" lines="two" density="compact">
              <v-list-item v-for="repair in pendingRepairs.slice(0, 5)" :key="repair.id" class="px-4">
                <template v-slot:prepend>
                  <v-icon color="warning">mdi-alert-circle</v-icon>
                </template>
                <v-list-item-title>{{ repair.description }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ findUnitName(repair.unit_id) }} - {{ formatDateForDisplay(repair.request_date) }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
            <v-alert v-else type="success" class="ma-3">
              No pending repairs at the moment.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Last 5 Payments -->
      <v-col cols="12" md="6" class="mb-6">
        <v-card elevation="2">
          <v-card-title class="d-flex align-center tableheader white--text">
            <v-icon left color="white">mdi-credit-card-check</v-icon>
            Recent Payments
            <v-spacer></v-spacer>
            <v-btn color="white" variant="text" to="/payments" size="small">
              View All
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-list v-if="recentPayments.length > 0" lines="two" density="compact">
              <v-list-item v-for="payment in recentPayments.slice(0, 5)" :key="payment.id" class="px-4">
                <template v-slot:prepend>
                  <v-icon color="success">mdi-check-circle</v-icon>
                </template>
                <v-list-item-title>{{ formatCurrency(payment.amount_paid) }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ payment.tenant_first_name }} {{ payment.tenant_last_name }} - {{ formatDateForDisplay(payment.payment_date) }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
            <v-alert v-else type="info" class="ma-3">
              No recent payments to display.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Last 5 Expenses -->
      <v-col cols="12" md="6" class="mb-6">
        <v-card elevation="2">
          <v-card-title class="d-flex align-center tableheader white--text">
            <v-icon left color="white">mdi-cash-multiple</v-icon>
            Recent Expenses
            <v-spacer></v-spacer>
            <v-btn color="white" variant="text" to="/expenses" size="small">
              View All
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-list v-if="recentExpenses.length > 0" lines="two" density="compact">
              <v-list-item v-for="expense in recentExpenses.slice(0, 5)" :key="expense.id" class="px-4">
                <template v-slot:prepend>
                  <v-icon color="error">mdi-cash-minus</v-icon>
                </template>
                <v-list-item-title>{{ formatCurrency(expense.amount) }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ expense.category }}, {{ findUnitName(expense.unit_id) }} - {{ formatDateForDisplay(expense.expense_date) }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
            <v-alert v-else type="info" class="ma-3">
              No recent expenses to display.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Incomplete Payments -->
      <v-col cols="12" class="mb-6">
        <v-card elevation="2">
          <v-card-title class="d-flex align-center tableheader white--text">
            <v-icon left color="white">mdi-clock-alert</v-icon>
            Incomplete Payments (Pending)
            <v-spacer></v-spacer>
            <v-btn color="white" variant="text" to="/payments" size="small">
              View All
              <v-icon right>mdi-arrow-right</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="pa-0">
            <v-data-table
              v-if="incompletePayments.length > 0"
              :headers="incompletePaymentsHeaders"
              :items="incompletePayments"
              :loading="loading"
              class="elevation-0"
              density="compact"
              hide-default-footer
            >
              <template v-slot:[`item.tenant_name`]="{ item }">
                {{ item.tenant_first_name }} {{ item.tenant_last_name }}
              </template>
              <template v-slot:[`item.amount_paid`]="{ value }">
                {{ formatCurrency(value) }}
              </template>
              <template v-slot:[`item.payment_date`]="{ value }">
                {{ formatDateForDisplay(value) }}
              </template>
              <template v-slot:[`item.due_date`]="{ value }">
                {{ value ? formatDateForDisplay(value) : 'Not set' }}
              </template>
            </v-data-table>
            <v-alert v-else type="success" class="ma-3">
              No incomplete payments at the moment.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import UnitService from '@/services/UnitService'
import MaintenanceRequestService from '@/services/MaintenanceRequestService'
import PaymentService from '@/services/PaymentService'
import ExpenseService from '@/services/ExpenseService'
import { formatCurrency, formatDateForDisplay } from '@/services/genFunctions'

const authStore = useAuthStore()

// Reactive data
const loading = ref(false)
const units = ref([])
const maintenanceRequests = ref([])
const payments = ref([])
const expenses = ref([])

// Computed properties
const availableUnits = computed(() => {
  return units.value.filter(unit => unit.status === 'Available')
})

const availableUnitsCount = computed(() => {
  return availableUnits.value.length
})

const pendingRepairs = computed(() => {
  return maintenanceRequests.value.filter(request => request.status === 'open')
})

const pendingRepairsCount = computed(() => {
  return pendingRepairs.value.length
})

const recentPayments = computed(() => {
  return [...payments.value]
    .filter(payment => payment.status === 'Completed')
    .sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date))
    .slice(0, 5)
})

const recentExpenses = computed(() => {
  return [...expenses.value]
    .sort((a, b) => new Date(b.expense_date) - new Date(a.expense_date))
    .slice(0, 5)
})

const incompletePayments = computed(() => {
  return payments.value.filter(payment => payment.status === 'Pending')
})

const incompletePaymentsCount = computed(() => {
  return incompletePayments.value.length
})

const totalPaymentsThisMonth = computed(() => {
  const currentMonth = new Date().getMonth()
  const currentYear = new Date().getFullYear()
  return payments.value.filter(payment => {
    const paymentDate = new Date(payment.payment_date)
    return paymentDate.getMonth() === currentMonth && 
           paymentDate.getFullYear() === currentYear &&
           payment.status !== 'pending'
  }).length
})

// Table headers for incomplete payments
const incompletePaymentsHeaders = [
  { title: 'Tenant', key: 'tenant_name', sortable: false },
  { title: 'Unit', key: 'unit_name', sortable: false },
  { title: 'Amount', key: 'amount_paid', sortable: false },
  // { title: 'Payment Date', key: 'payment_date', sortable: false },
  { title: 'Due Date', key: 'effective_start_date', sortable: false },
  { title: 'Details', key: 'payment_details', sortable: false }
]

// Helper functions
const findUnitName = (unitId) => {
  const unit = units.value.find(u => u.id === unitId)
  return unit ? unit.name : 'Unknown Unit'
}

// const formatCurrency = (amount) => {
//   if (!amount) return 'UGX 0.00'
//   return new Intl.NumberFormat('en-US', {
//     style: 'currency',
//     currency: 'UGX'
//   }).format(amount)
// }

// const formatDateForDisplay = (dateString) => {
//   if (!dateString) return 'N/A'
//   return new Date(dateString).toLocaleDateString('en-US', {
//     year: 'numeric',
//     month: 'short',
//     day: 'numeric'
//   })
// }

// Check if user has logged out
const hasLoggedOut = () => {
  return sessionStorage.getItem('user_logged_out') === 'true'
}

// Load data with authentication check
const loadDashboardData = async () => {
  // Check if user has logged out
  if (hasLoggedOut()) {
    console.log('User has logged out, skipping dashboard data load')
    return
  }

  // Check if user is authenticated
  if (!authStore.isLoggedIn) {
    console.log('User not authenticated, skipping dashboard data load')
    return
  }

  loading.value = true
  try {
    const [unitsData, maintenanceData, paymentsData, expensesData] = await Promise.all([
      UnitService.getUnits(),
      MaintenanceRequestService.getAll(),
      PaymentService.getPayments(),
      ExpenseService.getAll()
    ])
    
    units.value = unitsData
    maintenanceRequests.value = maintenanceData
    payments.value = paymentsData
    expenses.value = expensesData
  } catch (error) {
    console.error('Error loading dashboard data:', error)
    // If we get an authentication error, clear the session
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.log('Authentication error, clearing session')
      authStore.user = null
      authStore.isLoggedIn = false
      sessionStorage.setItem('user_logged_out', 'true')
    }
  } finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(async () => {
  loadDashboardData()
})
</script>

<style scoped>
.primary--text {
  color: var(--v-theme-primary) !important;
}

.grey--text.text--darken-1 {
  color: #757575 !important;
}

.tableheader {
  background-color: var(--v-theme-primary) !important;
}

.v-card {
  border-top: 3px solid var(--v-theme-primary);
}
</style>